import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "column items-center justify-evenly"
}
const _hoisted_2 = { class: "no-margin" }

import { App } from "@src-capacitor/@capacitor/app"
import { PluginListenerHandle } from "@src-capacitor/@capacitor/core"
import { Nfc, NfcUtils } from "@src-capacitor/@capawesome-team/capacitor-nfc"
import SfxBtn from "src/components/button/SfxBtn.vue"
import { Log } from "src/util/classes/log"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { onBeforeMount, onUnmounted, ref } from "vue"

/**
 * Listens for NFC scans and emits them back their value with the **passData** event,
 * as well as the status of NFC with the **nfcEnabled** event.
 * Continuously checks the status of NFC.
 * If NFC is not enabled or found, it displays a button which prompts the user to try again.
 *
 * @event passData The NFC scan data.
 * @event nfcEnabled The reason for failure to find NFC.
 */

//region Props and Emits

const CHECK_NFC_INTERVAL_TIME = 15000 //ms


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxScannerNfc',
  emits: ["nfcEnabled", "passData"],
  setup(__props, { emit: __emit }) {

const emit = __emit

//endregion

//region Variables

const nfcEnabled = ref<boolean>(false)
const canAttemptTurningOn = ref(false)
let pausedInterval = false
let checkNfcInterval: ReturnType<typeof setInterval> | undefined
const scannerSessionActive = ref(false)
let stateListener: PluginListenerHandle
let nfcTagScannedListener: PluginListenerHandle

//endregion

//region Lifecycle

/**
 * Make an initial call to {@link checkNfcStatus} and set an interval timer for continuous calling.
 */
onBeforeMount(async () => {
  const nfcSupported = await Nfc.isSupported()
  if (!nfcSupported) {
    // If it is not even supported, then don't bother executing the rest of the components code
    nfcEnabled.value = false
    return
  } else {
    canAttemptTurningOn.value = true
  }

  stateListener = await startAppStateListener()
  checkNfcInterval = startNfcStatusCheckInterval()
  // Initiate the first status check, since the interval does not trigger the first one
  await checkNfcStatus()
})

/**
 * Removes scanner subscription and clears timer interval
 */
onUnmounted(async () => {
  if (scannerSessionActive.value) await Nfc.stopScanSession()
  clearInterval(checkNfcInterval)
  stateListener?.remove()
  nfcTagScannedListener?.remove()
})

//endregion

//region Functions

function startNfcStatusCheckInterval(): ReturnType<typeof setInterval> {
  return setInterval(() => {
    // Pause interval
    if (pausedInterval) return

    void checkNfcStatus()
  }, CHECK_NFC_INTERVAL_TIME)
}

/**
 * We want to check for NFC status if the app is currently active.
 * If the app is in the background, then pause the check status interval.
 */
function startAppStateListener(): Promise<PluginListenerHandle> {
  return App.addListener("appStateChange", ({ isActive }) => {
    // If we are in the background, pause the interval, otherwise check for nfc status
    if (isActive) {
      void checkNfcStatus()
    } else {
      pausedInterval = true
    }
  })
}

/**
 * Checks if NFC is available and enabled.
 * If NFC is working it calls {@link initScanning} and changes *"nfcEnabled"* to true.
 * If for whatever reason NFC is not enabled it changes *"nfcEnabled"* to false.
 * Finally, it emits *"nfcEnabled"* value with the **nfcEnabled** event.
 */
async function checkNfcStatus() {
  try {
    pausedInterval = false
    const status = await Nfc.isEnabled()
    nfcEnabled.value = status.isEnabled
    await initScanning()
  } catch (error) {
    nfcEnabled.value = false

    Log.debug({
      message: "NFC Reader Error",
      data: { error },
    })
  } finally {
    emit("nfcEnabled", nfcEnabled.value)
  }
}

/**
 * Subscribes to the ***NFC.readerMode*** function.
 * On successful scan, it converts the data to human-readable hex string and emits it with the **passData** event.
 * On error, the object is sent to Sentry
 */
async function initScanning() {
  // We already have a session active, we can NOT have multiple on
  if (scannerSessionActive.value) return
  const utils = new NfcUtils()

  await Nfc.startScanSession()
  scannerSessionActive.value = true

  nfcTagScannedListener = await Nfc.addListener("nfcTagScanned", ({ nfcTag }) => {
    if (nfcTag.id) {
      const data = utils.convertBytesToHex({ bytes: nfcTag.id }).hex
      const value = data.startsWith("0x") ? data.slice(2) : data
      emit("passData", value)
    }
  })
}

async function retryNFCAvailability() {
  // NFC is disabled and we can try to enabled it
  await Nfc.openSettings()
}

//endregion

return (_ctx: any,_cache: any) => {
  return (!nfcEnabled.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", null, _toDisplayString(_ctx.$t("sfxScanners.nfc_warning")), 1),
        (canAttemptTurningOn.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createVNode(SfxBtn, {
                icon: _unref(Icons).ARROW_ROTATE_RIGHT,
                class: "text-overline text-uppercase q-mt-md q-mb-sm",
                color: "white",
                "text-color": "navy-base",
                onClick: retryNFCAvailability
              }, null, 8, ["icon"]),
              _createElementVNode("p", _hoisted_2, _toDisplayString(_ctx.$t("sfxButtons.btn_retry")), 1)
            ], 64))
          : _createCommentVNode("", true)
      ]))
    : _createCommentVNode("", true)
}
}

})